import { announce } from "../lib/announce.ts";
import { defineComponent } from "../lib/component.ts";

const LOCK_THRESHOLD = 10;
const FLICK_VELOCITY = 0.3;
const COMMIT_FRACTION = 0.33;

interface HighlightCraftRefs {
  track?: HTMLElement;

  slider?: HTMLElement;

  largeContainer?: HTMLElement;

  blocksContainer?: HTMLElement;

  titleDisplay?: HTMLAnchorElement;

  prevBtn?: HTMLButtonElement;

  nextBtn?: HTMLButtonElement;

  smallSlides?: HTMLElement[];

  largeSlides?: HTMLElement[];
}

interface DragSample {
  x: number;
  t: number;
}

class HighlightCraft extends defineComponent<HighlightCraftRefs>()([
  "track",
  "slider",
  "largeContainer",
  "titleDisplay",
]) {
  #currentIndex = 0;

  #slideCount = 0;

  #transitioning = false;

  #hasClones = false;

  #wrappers: HTMLElement[] = [];

  // Track position in the cloned slide array (0 = prepended last-clone,
  // 1..slideCount = real slides, slideCount+1 = appended first-clone).
  #trackPosition = 1;

  #dragStartX: number | null = null;
  #dragStartY: number | null = null;
  #dragging = false;
  // null = undecided, true = horizontal, false = vertical
  #dragDirection: boolean | null = null;
  #dragDeltaX = 0;
  #dragSamples: DragSample[] = [];

  connectedCallback(): void {
    super.connectedCallback();

    const smallSlides = this.refs.smallSlides;
    const largeSlides = this.refs.largeSlides;
    if (!Array.isArray(smallSlides) || !Array.isArray(largeSlides) || smallSlides.length === 0)
      return;

    this.#slideCount = smallSlides.length;
    // Each child block is wrapped by Shopify in a `.shopify-block` element used
    // as the editor's selection target; move that wrapper into the slider so the
    // outline sits at the slide's visible position.
    this.#wrappers = smallSlides.map((slide) => {
      const wrapper = slide.closest(".shopify-block");
      return wrapper instanceof HTMLElement ? wrapper : slide;
    });
    this.#buildSlider(smallSlides, largeSlides);
    this.#syncState();

    document.addEventListener("shopify:block:select", this.#onBlockSelect, {
      signal: this.signal,
    });

    if (this.#slideCount > 1) {
      this.refs.prevBtn?.addEventListener("click", this.#prev, {
        signal: this.signal,
      });
      this.refs.nextBtn?.addEventListener("click", this.#next, {
        signal: this.signal,
      });
      this.refs.slider.addEventListener("touchstart", this.#onTouchStart, {
        passive: true,
        signal: this.signal,
      });
      this.refs.slider.addEventListener("touchmove", this.#onTouchMove, {
        passive: false,
        signal: this.signal,
      });
      this.refs.slider.addEventListener("touchend", this.#onTouchEnd, {
        passive: true,
        signal: this.signal,
      });
      this.refs.slider.addEventListener("touchcancel", this.#onTouchEnd, {
        passive: true,
        signal: this.signal,
      });
      this.addEventListener("keydown", this.#onKeyDown, {
        signal: this.signal,
      });
      this.refs.track.addEventListener("transitionend", this.#onTransitionEnd, {
        signal: this.signal,
      });
    }
  }

  #buildSlider(smallSlides: HTMLElement[], largeSlides: HTMLElement[]): void {
    const track = this.refs.track;
    const largeContainer = this.refs.largeContainer;
    const wrappers = this.#wrappers;

    track.replaceChildren();
    largeContainer.replaceChildren();
    this.#hasClones = false;
    this.#transitioning = false;
    if (this.#currentIndex >= wrappers.length) this.#currentIndex = 0;

    for (const slide of largeSlides) {
      largeContainer.appendChild(slide);
    }

    if (wrappers.length > 1) {
      const lastSource = wrappers[wrappers.length - 1].cloneNode(true);
      const firstSource = wrappers[0].cloneNode(true);
      if (!(lastSource instanceof HTMLElement) || !(firstSource instanceof HTMLElement)) return;
      const lastClone = lastSource;
      const firstClone = firstSource;
      for (const clone of [lastClone, firstClone]) {
        clone.removeAttribute("id");
        clone.removeAttribute("data-shopify-editor-block");
        for (const el of clone.querySelectorAll("[data-shopify-editor-block]")) {
          el.removeAttribute("data-shopify-editor-block");
        }
        for (const el of clone.querySelectorAll("[ref]")) {
          el.removeAttribute("ref");
        }
        for (const el of clone.querySelectorAll("[data-block-id]")) {
          el.removeAttribute("data-block-id");
        }
        clone.setAttribute("aria-hidden", "true");
        clone.inert = true;
      }

      track.appendChild(lastClone);
      for (const wrapper of wrappers) {
        track.appendChild(wrapper);
      }
      track.appendChild(firstClone);
      this.#hasClones = true;
    } else {
      track.appendChild(wrappers[0]);
    }

    for (const slide of smallSlides) {
      const link = slide.querySelector("a");
      const title = slide.dataset.title;
      if (link && title) link.setAttribute("aria-label", title);
    }

    this.#trackPosition = this.#hasClones ? 1 : 0;
    this.#applyTrackPosition(false);
  }

  #applyTrackPosition(animate: boolean): void {
    const track = this.refs.track;
    if (!animate) {
      track.style.transition = "none";
      track.style.transform = `translateX(-${this.#trackPosition * 100}%)`;
      track.getBoundingClientRect();
      track.style.transition = "";
    } else {
      track.style.transform = `translateX(-${this.#trackPosition * 100}%)`;
    }
  }

  #syncState(): void {
    const largeSlides = this.refs.largeSlides;
    if (!Array.isArray(largeSlides)) return;

    for (let i = 0; i < largeSlides.length; i++) {
      const active = i === this.#currentIndex;
      largeSlides[i].classList.toggle("is-active", active);
      largeSlides[i].inert = !active;
      this.#toggleMedia(largeSlides[i], active);
    }

    const smallSlides = this.refs.smallSlides;
    if (Array.isArray(smallSlides)) {
      for (let i = 0; i < smallSlides.length; i++) {
        const active = i === this.#currentIndex;
        this.#wrappers[i].inert = !active;
        this.#toggleMedia(smallSlides[i], active);
      }
    }

    const activeSmall = Array.isArray(smallSlides) ? smallSlides[this.#currentIndex] : null;
    if (activeSmall) {
      const title = activeSmall.dataset.title ?? "";
      const url = activeSmall.dataset.url ?? "";
      this.refs.titleDisplay.textContent = title;
      if (url) {
        this.refs.titleDisplay.href = url;
      } else {
        this.refs.titleDisplay.removeAttribute("href");
      }
      this.refs.titleDisplay.hidden = !title;
    }

    this.#preloadAdjacent();
  }

  #preloadAdjacent(): void {
    const smallSlides = this.refs.smallSlides;
    const largeSlides = this.refs.largeSlides;
    if (!Array.isArray(smallSlides) || !Array.isArray(largeSlides)) return;

    const indices = [
      this.#currentIndex,
      (this.#currentIndex + 1) % this.#slideCount,
      (this.#currentIndex - 1 + this.#slideCount) % this.#slideCount,
    ];

    for (const idx of indices) {
      for (const img of smallSlides[idx].querySelectorAll("img")) {
        img.loading = "eager";
      }
      for (const img of largeSlides[idx].querySelectorAll("img")) {
        img.loading = "eager";
      }
    }
  }

  #toggleMedia(container: HTMLElement, active: boolean): void {
    const video = container.querySelector("video");
    if (!video) return;
    if (active) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  #navigate(direction: number): void {
    if (this.#transitioning) return;
    this.#transitioning = true;

    this.#currentIndex = (this.#currentIndex + direction + this.#slideCount) % this.#slideCount;
    this.#trackPosition += direction;
    this.#applyTrackPosition(true);
    this.#syncState();
    this.#announceSlide();
  }

  #prev = (): void => {
    this.#navigate(-1);
  };

  #next = (): void => {
    this.#navigate(1);
  };

  #onTransitionEnd = (e: TransitionEvent): void => {
    if (e.target !== this.refs.track || e.propertyName !== "transform") return;
    this.#transitioning = false;

    if (this.#trackPosition === 0) {
      this.#trackPosition = this.#slideCount;
      this.#applyTrackPosition(false);
    } else if (this.#trackPosition === this.#slideCount + 1) {
      this.#trackPosition = 1;
      this.#applyTrackPosition(false);
    }
  };

  #onTouchStart = (e: TouchEvent): void => {
    if (this.#transitioning) return;
    this.#dragStartX = e.touches[0].clientX;
    this.#dragStartY = e.touches[0].clientY;
    this.#dragging = false;
    this.#dragDirection = null;
    this.#dragDeltaX = 0;
    this.#dragSamples = [{ x: e.touches[0].clientX, t: Date.now() }];
  };

  #onTouchMove = (e: TouchEvent): void => {
    if (this.#dragStartX === null || this.#dragStartY === null) return;

    const x = e.touches[0].clientX;
    const y = e.touches[0].clientY;
    const deltaX = x - this.#dragStartX;
    const deltaY = y - this.#dragStartY;

    if (this.#dragDirection === null) {
      if (Math.abs(deltaY) > LOCK_THRESHOLD) {
        this.#dragDirection = false;
      } else if (Math.abs(deltaX) > LOCK_THRESHOLD) {
        this.#dragDirection = true;
      } else {
        return;
      }
    }

    if (!this.#dragDirection) return;

    e.preventDefault();
    this.#dragging = true;
    this.#dragDeltaX = deltaX;

    this.#dragSamples.push({ x, t: Date.now() });
    if (this.#dragSamples.length > 5) this.#dragSamples.shift();

    const track = this.refs.track;
    track.style.transition = "none";
    track.style.transform = `translateX(calc(-${this.#trackPosition * 100}% + ${deltaX}px))`;
  };

  #onTouchEnd = (_e: TouchEvent): void => {
    if (!this.#dragging) {
      this.#dragStartX = null;
      this.#dragStartY = null;
      return;
    }

    const velocity = this.#computeVelocity();
    const slideWidth = this.refs.slider.offsetWidth;
    const absDelta = Math.abs(this.#dragDeltaX);
    const absVelocity = Math.abs(velocity);

    let direction = 0;
    if (absVelocity > FLICK_VELOCITY) {
      direction = velocity < 0 ? 1 : -1;
    } else if (absDelta > slideWidth * COMMIT_FRACTION) {
      direction = this.#dragDeltaX < 0 ? 1 : -1;
    }

    this.refs.track.style.transition = "";

    if (direction !== 0) {
      this.#transitioning = true;
      this.#currentIndex = (this.#currentIndex + direction + this.#slideCount) % this.#slideCount;
      this.#trackPosition += direction;
      this.#applyTrackPosition(true);
      this.#syncState();
      this.#announceSlide();
    } else if (absDelta > 0) {
      this.#transitioning = true;
      this.#applyTrackPosition(true);
    }

    this.#dragStartX = null;
    this.#dragStartY = null;
    this.#dragging = false;
    this.#dragDirection = null;
  };

  #computeVelocity(): number {
    const samples = this.#dragSamples;
    if (samples.length < 2) return 0;
    const first = samples[0];
    const last = samples[samples.length - 1];
    const dt = last.t - first.t;
    if (dt === 0) return 0;
    return (last.x - first.x) / dt;
  }

  #onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      this.#prev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      this.#next();
    }
  };

  #onBlockSelect = (e: DocumentEventMap["shopify:block:select"]): void => {
    const { blockId } = e.detail;

    const smallSlides = this.refs.smallSlides;
    if (!Array.isArray(smallSlides)) return;

    const index = smallSlides.findIndex((slide) => slide.dataset.blockId === blockId);
    if (index === -1 || index === this.#currentIndex) return;

    this.#currentIndex = index;
    this.#trackPosition = this.#hasClones ? index + 1 : index;
    this.#applyTrackPosition(false);
    this.#syncState();
  };

  #announceSlide(): void {
    const template = this.dataset.slideAnnouncement;
    if (!template) return;

    const activeSmall = Array.isArray(this.refs.smallSlides)
      ? this.refs.smallSlides[this.#currentIndex]
      : null;
    const title = activeSmall?.dataset.title ?? "";

    const message = template
      .replace("{{ title }}", title)
      .replace("{{ current }}", String(this.#currentIndex + 1))
      .replace("{{ total }}", String(this.#slideCount));
    announce(message);
  }
}

if (!customElements.get("highlight-craft")) {
  customElements.define("highlight-craft", HighlightCraft);
}
